import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { SiteSettingsProvider, useSiteSettings } from './context/SiteSettingsContext';
import FundraiserListPage from './components/FundraiserListPage';
import FundraiserDetailPage from './components/FundraiserDetailPage';
import SignUpPage from './components/SignUpPage';
import AdminPage from './components/AdminPage';
import AdminLogin from './components/AdminLogin';
import AboutMission from './components/AboutMission';

// Data is now managed by the backend server

function AppContent() {
  const { settings } = useSiteSettings();
  const [fundraisers, setFundraisers] = useState([]);
  const [pendingApplicants, setPendingApplicants] = useState([]);
  const [isDemoMode, setIsDemoMode] = useState(false);

  const apiUrl = process.env.REACT_APP_API_URL;

  // Check if we're in demo mode by checking the hostname
  useEffect(() => {
    const hostname = window.location.hostname;
    if (hostname.includes('demo.') || process.env.REACT_APP_DEMO_MODE === 'true') {
      setIsDemoMode(true);
    }
  }, []);

  const fetchFundraisers = async () => {
    try {
      const response = await fetch(`${apiUrl}/fundraisers`);
      if (response.ok) {
        const data = await response.json();
        setFundraisers(data);
      }
    } catch (error) {
      console.error('Error fetching fundraisers:', error);
    }
  };

  const fetchPendingApplicants = async (authToken = null) => {
    try {
      const headers = authToken ? { 'Authorization': `Bearer ${authToken}` } : {};
      const response = await fetch(`${apiUrl}/pending-applicants`, { headers });
      if (response.ok) {
        const data = await response.json();
        setPendingApplicants(data);
      }
    } catch (error) {
      console.error('Error fetching pending applicants:', error);
    }
  };

  useEffect(() => {
    fetchFundraisers();
    // Don't fetch pending applicants on startup - only when admin is authenticated
    
    // Refresh data every 30 seconds
    const interval = setInterval(() => {
      fetchFundraisers();
      // Only refresh pending applicants if we have them (means admin is logged in)
      if (pendingApplicants.length > 0) {
        fetchPendingApplicants();
      }
    }, 30000);
    
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addPendingApplicant = async (applicant) => {
    try {
      const response = await fetch(`${apiUrl}/applicants`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(applicant),
      });
      
      if (response.ok) {
        await fetchPendingApplicants(); // Refresh the list
      }
    } catch (error) {
      console.error('Error adding applicant:', error);
    }
  };

  const approveFundraiser = async (applicantId) => {
    try {
      const response = await fetch(`${apiUrl}/approve-applicant/${applicantId}`, {
        method: 'POST',
      });
      
      if (response.ok) {
        await fetchFundraisers(); // Refresh fundraisers
        await fetchPendingApplicants(); // Refresh pending list
      }
    } catch (error) {
      console.error('Error approving applicant:', error);
    }
  };

  const updateFundraiserAmount = async (fundraiserId, newAmount) => {
    // Trigger a refresh to get updated amounts from Stripe
    await fetchFundraisers();
  };

  return (
    <Router>
      <div className="min-h-screen bg-gray-50">
        {/* Custom Banner */}
        {settings.banner_enabled && settings.banner_text && (
          <div className="bg-blue-600 text-white py-2 px-4 text-center font-semibold text-sm">
            {settings.banner_text}
          </div>
        )}

        {/* Demo Mode Banner */}
        {isDemoMode && (
          <div className="bg-yellow-500 text-gray-900 py-2 px-4 text-center font-semibold text-sm">
            🎮 DEMO MODE - Login with: admin / password | Limited to 5 fundraisers & 2 admins
          </div>
        )}

        <nav className="text-white p-4" style={{ backgroundColor: settings.primary_color }}>
          <div className="container mx-auto flex justify-between items-center">
            <div className="flex items-center gap-3">
              {settings.logo_enabled && settings.logo_url && (
                <img src={settings.logo_url} alt="Logo" className="h-8 object-contain" />
              )}
              <h1 className="text-xl font-bold">{settings.site_title}</h1>
            </div>
            <div className="space-x-4">
              <Link to="/" className="hover:opacity-80">Home</Link>
              <Link to="/about" className="hover:opacity-80">About Mission</Link>
              <Link to="/signup" className="hover:opacity-80">Join Team</Link>
            </div>
          </div>
        </nav>

          <Routes>
            <Route path="/" element={<FundraiserListPage fundraisers={fundraisers} />} />
            <Route path="/about" element={<AboutMission />} />
            <Route path="/fundraiser/:id" element={
              <FundraiserDetailPage
                fundraisers={fundraisers}
                onDonationComplete={updateFundraiserAmount}
              />
            } />
            <Route path="/signup" element={<SignUpPage onSubmit={addPendingApplicant} />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={
              <AdminPage
                pendingApplicants={pendingApplicants}
                onApprove={approveFundraiser}
              />
            } />
          </Routes>

        {/* Footer */}
        <footer className="bg-gray-800 text-white py-6 mt-12">
          <div className="container mx-auto px-4 text-center">
            {settings.footer_text ? (
              <p className="text-sm text-gray-300">{settings.footer_text}</p>
            ) : (
              <>
                <p className="text-sm text-gray-300">
                  Brought to you by <span className="font-semibold text-white">Jaron Wilson</span>
                </p>
                <p className="text-xs text-gray-400 mt-2">
                  {settings.site_title} &copy; {new Date().getFullYear()}
                </p>
              </>
            )}
          </div>
        </footer>
      </div>
    </Router>
  );
}

function App() {
  return (
    <AuthProvider>
      <SiteSettingsProvider>
        <AppContent />
      </SiteSettingsProvider>
    </AuthProvider>
  );
}

export default App;